import {
  CurrentUserApiResponse,
  GroupsListApiResponse,
  OrganizationApiResponse,
  UserBasicInformation,
} from '@/types/api.types';
import { DemoDataEvent } from 'klayowebshared';
import { track } from '@/common/track';
import { DEFAULT_DATASET_CODE, setRuntimeDataset } from '../datasets';
import { MY_TEAM_DATASET_CODE } from '../datasets/myTeam';
import { PreviewDataset } from '../preview.types';
import { armPreview, disarmPreview, isPreviewArmed } from '../mock/previewMode';
import { DemoView, withDemoParam } from './demoParam';

// The reusable "preview identity" layer for the Try It Now sandbox.
//
// A trial prospect must explore the REAL app pages on synthetic data while still seeing THEIR OWN
// name + org in the chrome. We achieve that by capturing the real display identity before a hard
// reload into preview, persisting it in sessionStorage, then rebuilding a full synthetic
// CurrentUser/Organization/Groups response that KEEPS the real display fields and OVERRIDES only
// the capability flags needed to render the sample pages.
//
// These builders feed BOTH the seed layer (queryClient.setQueryData for instant first paint) and
// the serve layer (the axios mock, so refetches stay stable) — see bootstrapPreview + mock/sections/identity.

const INTENT_KEY = 'klayo.preview.intent';
const ONBOARDED_AT = '2020-01-01T00:00:00.000Z'; // any past date bypasses onboarding

/**
 * Which control launched a demo — the Solution Finder card, or the app user-menu's "Switch to demo
 * data". Both are offered to the same audience (each gated on `organization.isTrial`), so this says
 * where the click came from and nothing about who was clicking.
 *
 * Reported on `demo_data:entered` only. The queued stamp that feeds `demo_data:viewed` carries
 * solution codes alone, so the completion leg cannot be broken down by entry point.
 */
export type DemoSource = 'solution_finder' | 'app_menu';

/** The real display identity captured from the live session before reloading into preview. */
export interface PreviewIdentity {
  fullName: string;
  firstName: string;
  lastName: string;
  email: string;
  employeeId: string;
  orgName: string;
}

export interface PreviewIntent {
  solutionCode: string;
  datasetCode: string;
  identity: PreviewIdentity;
  /** The real app URL the prospect launched from — Exit returns them here, not a hardcoded page. */
  returnTo?: string;
  /** Was the REAL logged-in user a training manager? Gates the "Show my team" toggle. Captured at
   *  entry (the armed session's synthetic identity can't tell us this). */
  realIsTrainingManager?: boolean;
}

// --- sessionStorage intent --------------------------------------------------------------

export function readIntent(): PreviewIntent | null {
  try {
    const raw = sessionStorage.getItem(INTENT_KEY);
    return raw ? (JSON.parse(raw) as PreviewIntent) : null;
  } catch {
    return null;
  }
}

function writeIntent(intent: PreviewIntent): void {
  sessionStorage.setItem(INTENT_KEY, JSON.stringify(intent));
}

export function clearIntent(): void {
  sessionStorage.removeItem(INTENT_KEY);
}

// Maps a solution code to the REAL app route its preview lands on. Adding a preview slice = add a
// mapping here. Unknown codes fall back to the workforce (Team) page. (Two solutions may share a
// route.)
// Landing routes per the PO's mapping. All solutions land on a Team tab (People / Competencies /
// Tasks / Training). Training Needs Assessment lands on the Training tab with the "Show gaps only"
// toggle pre-selected (the `gaps=1` query param — honoured by useTeamTrainingSection).
const SOLUTION_TARGETS: Record<string, string> = {
  'workforce-skills-inventory': '/team/competencies',
  'risk-assessment-mitigation': '/team',
  // Compliance is the regulation-task story, so it lands on Tasks rather than People.
  'workforce-compliance-management': '/team/tasks',
  'realtime-training-needs-assessment': '/team/trainings?gaps=1',
  'succession-planning': '/team',
  'targeted-training-planning': '/team/competencies',
};

// Every landing URL carries `demo=<solutionCode>` (and `view`) so Userpilot can target the demo
// pages by URL — see demoParam.ts. Unknown codes still get stamped: the param records where the
// prospect came from, independently of which route we could map them to.
//
// Stamping here is not redundant with App's correction. switchPreviewDataset hard-navigates, so
// this URL is what Userpilot reads at boot — before App has mounted and can correct anything.
// Without it the first page view of a toggled session is reported un-parameterised.
// `view` is required, not defaulted: the caller always knows which dataset is armed, and a default
// would silently label a my-team session "sample" (and send it to the solution's tab rather than
// People) at the one moment Userpilot reads the URL.
export function targetPathFor(solutionCode: string, view: DemoView): string {
  return withDemoParam(SOLUTION_TARGETS[solutionCode] ?? '/team', solutionCode, view);
}

// Maps a solution code to its synthetic dataset. All share the aviation dataset today; a solution
// can diverge later by registering a new PreviewDataset and pointing here. Unknown → default.
const SOLUTION_DATASET_MAP: Record<string, string> = {
  'workforce-skills-inventory': DEFAULT_DATASET_CODE,
  'risk-assessment-mitigation': DEFAULT_DATASET_CODE,
  'workforce-compliance-management': DEFAULT_DATASET_CODE,
  'realtime-training-needs-assessment': DEFAULT_DATASET_CODE,
  'succession-planning': DEFAULT_DATASET_CODE,
  'targeted-training-planning': DEFAULT_DATASET_CODE,
};

export function datasetCodeForSolution(solutionCode: string): string {
  return SOLUTION_DATASET_MAP[solutionCode] ?? DEFAULT_DATASET_CODE;
}

// Nav slugs the preview sandbox exposes — derived from the target routes so it auto-tracks new
// preview slices. While armed, the app menu is restricted to these so a prospect (whose synthetic
// identity has broad admin permissions) can't wander into unmocked areas.
export const PREVIEW_NAV_SLUGS = new Set([
  ...Object.values(SOLUTION_TARGETS).map((p) => p.replace(/^\//, '').split('/')[0]),
  'groups', // My Groups is explorable in preview alongside the solution's target page
]);

// --- builders (the single source consumed by seed + serve) ------------------------------

function basicInformation(identity: PreviewIdentity): UserBasicInformation {
  return {
    // KEEP REAL — display identity shown in the chrome.
    employeeId: identity.employeeId,
    firstName: identity.firstName,
    lastName: identity.lastName,
    fullName: identity.fullName,
    email: identity.email,
    // FORCE — never request a real avatar image (bypasses the mock); renders initials instead.
    hasProfilePicture: false,
    // OVERRIDE — synthetic capabilities so the real Team People + Competencies tabs render.
    isTeamLead: true, // non-group /team gates CONTENT on isTeamLead (admin alone => empty state)
    isOrganizationAdmin: true,
    isBillingUser: false,
    isInstructor: false,
    isParticipant: false,
    isFullAccess: true,
    isMyKlayoUser: false,
    // Unlocks the Manage Training area (/settings/learning) for the Targeted Training Planning
    // preview — full tabs (Courses + Students) and the balance widget. Safe to set globally: on the
    // other preview pages (Team/Company) nothing keys off the CURRENT user's isTrainingManager
    // (the App.tsx use is gated by !isFullAccess; EmployeeTable reads a per-ROW flag), and the
    // Manage nav is reachable in every preview anyway via PREVIEW_NAV_SLUGS.
    isTrainingManager: true,
    hasJob: true,
    hasGroup: true, // show the My Groups nav (gated on user.hasGroup)
    status: 2, // Active
    organizationAcceptTermsAndConditions: true, // dodge the signup redirect
    onboardingCompletedDate: ONBOARDED_AT, // dodge the onboarding redirect
  };
}

export function buildUser(identity: PreviewIdentity): CurrentUserApiResponse {
  const basic = basicInformation(identity);
  return {
    // Both envelopes carry the SAME employeeId => CurrentUser.impersonating computes false.
    loggedInUser: {
      basicInformation: basic,
      permissions: {
        isDeveloper: false,
        canChangeUser: false,
        canChangeOrg: false,
        hasDemoEmployeeId: false,
      },
    },
    activeUser: { basicInformation: basic },
  };
}

export function buildOrg(identity: PreviewIdentity): OrganizationApiResponse {
  // "Show my team" mode renders the TM's real students: real names + the org's course catalogue on
  // the Training tab, but the People job/gap figures zeroed (jobless dataset) and the Competencies
  // and Tasks tabs deliberately EMPTY — the demo's point is to show the prospect they haven't mapped
  // competencies/tasks yet ("nothing here yet"). So both tabs are SHOWN (Tasks via hasRegulationTasks
  // below, Competencies always in Team.getActivePages) but carry no data. All core features stay on
  // so `isEnabledCoreFeatures` is true and the /team route's access gate (pages.tsx) passes.
  return {
    organization: {
      organizationId: 'preview-org',
      name: identity.orgName, // KEEP REAL — chrome shows the prospect's own org name
      showProficiency: true,
      isDemo: false, // avoid the "Demo Company" chrome label
      status: 0, // Trial => isTrial true (required by /try + team gates)
      // Far-future so any trial-countdown chrome shows a sane value rather than blank/expired.
      trialEndDate: '2099-12-31T00:00:00.000Z',
      hasIntegration: true, // shows the Team Training tab (with enabledFeatures.Training)
      hasActiveIntegration: false,
      hasMultipleIntegrations: false,
      hasRegulationTasks: true, // Tasks tab shown for both datasets (empty in my-team mode)
      hasRegulations: false, // Regulations tab stays hidden (out of scope)
      enableJobMatching: false, // hide the irrelevant Job-Matching sub-tab in the profile dialog
      allowJobMatchingConfig: false,
      enableStandardsLibrary: false,
      isPaidPlan: false,
      // ALL 5 core features true for BOTH datasets → isEnabledCoreFeatures true → the /team route
      // (and its nav) is accessible. hasRegulations stays false so the Regulations TAB is hidden
      // even though the licence feature is on. Nothing hides Tasks or Competencies in my-team: both
      // tabs are shown and simply carry no data (see the note above) — and the Tasks tab has to stay
      // shown regardless, since Compliance Management now lands on it.
      enabledFeatures: {
        Competencies: true,
        Employees: true,
        Jobs: true,
        Regulations: true,
        Tasks: true,
        MyKlayo: true,
        Training: true,
        KlayoIntelligence: false,
      },
    },
  };
}

// Dataset-driven so the seed (bootstrapPreview) and serve (/MyGroup) stay identical.
export function buildGroups(dataset?: PreviewDataset): GroupsListApiResponse {
  return {
    groups: (dataset?.groups ?? []).map((g) => ({
      groupId: g.groupId,
      name: g.name,
      description: g.description,
      hasManagePermission: true,
      memberCount: g.memberEmployeeIds.length,
    })),
  };
}

// --- arming lifecycle -------------------------------------------------------------------

/**
 * Runs at the very top of index.tsx (before posthog/analytics init) so the egress kill-switch can
 * see isPreviewArmed() synchronously. Preview is a session-wide mode: armed whenever the intent
 * exists. The intent is written ONLY by enterPreview (a trial prospect clicking Try It Now) and
 * removed by exitPreview, so a normal/paying session never has it. This makes the demo refresh-safe
 * on any route (e.g. /team) rather than coupling armed-ness to the /try path.
 */
export function primeArmedFromIntent(): void {
  const intent = readIntent();
  if (!intent) return;

  // Only arm when we actually land on a preview route. Browser back/forward (or a typed URL) can
  // drop an armed session onto a non-preview page (e.g. /solution-finder) whose REAL data fetches
  // would then hit the fail-closed mock and error. Treat that as an exit: clear the intent and boot
  // the normal app. The /try entry + the restricted nav slugs are the only legitimate armed routes.
  // (Clearing also stops a lingering intent from later hijacking a real /team etc. on refresh.)
  const topSegment = window.location.pathname.replace(/^\//, '').split('/')[0];
  if (topSegment !== 'try' && !PREVIEW_NAV_SLUGS.has(topSegment)) {
    clearIntent();
    return;
  }

  // "Show my team" arms LATER — bootstrapPreview must first do the real student read while UN-armed
  // (so it's a normal authenticated request, not a fail-closed rejection). Leaving it un-armed here
  // is safe: the intent still exists, so hasPreviewIntent() keeps telemetry suppressed meanwhile.
  if (intent.datasetCode === MY_TEAM_DATASET_CODE) return;

  // `?? datasetCodeForSolution` covers intents written before datasetCode existed.
  armPreview(
    intent.solutionCode,
    intent.datasetCode ?? datasetCodeForSolution(intent.solutionCode)
  );
}

/**
 * True when this session is ENTERING (or in) a preview — i.e. a preview intent survived
 * primeArmedFromIntent's route guard. Use this (not isPreviewArmed) for the index.tsx egress
 * kill-switch: "Show my team" is intentionally NOT armed yet during its un-armed bootstrap read,
 * but telemetry must already be suppressed so the prospect's identity never leaves the browser.
 */
export function hasPreviewIntent(): boolean {
  return readIntent() !== null;
}

const PREVIEW_UPID_KEY = 'klayo.preview.upid';

/**
 * A per-session synthetic Userpilot id for the "Try It Now" preview. Onboarding tours must run on
 * the demo pages, but we must never hand Userpilot the prospect's real employeeId (the preview
 * chrome KEEPS their real name/email — see buildUser). A fresh random id per session means each demo
 * is a brand-new anonymous Userpilot user, so tours always trigger (no "already seen" bleed between
 * prospects) while zero real identifier leaves the browser. Session-scoped so route changes within
 * one demo keep the same id.
 */
let inMemoryPreviewUpid: string | null = null;

export function previewUserpilotId(): string {
  // Guarded like readIntent: sessionStorage (private mode/quota) and crypto.randomUUID (non-secure
  // context / old browser) can throw. This runs on the render path via onUserLoaded, so a throw must
  // never break the prospect's page — fall back to an in-memory id for the tab's lifetime.
  const mint = () =>
    typeof crypto !== 'undefined' && crypto.randomUUID
      ? `preview-${crypto.randomUUID()}`
      : `preview-${Date.now()}-${Math.random().toString(36).slice(2)}`;
  try {
    let id = sessionStorage.getItem(PREVIEW_UPID_KEY);
    if (!id) {
      id = mint();
      sessionStorage.setItem(PREVIEW_UPID_KEY, id);
    }
    return id;
  } catch {
    inMemoryPreviewUpid = inMemoryPreviewUpid ?? mint();
    return inMemoryPreviewUpid;
  }
}

// --- pending demo stamp -----------------------------------------------------------------

const PENDING_DEMO_STAMP_KEY = 'klayo.preview.pendingDemoStamp';

interface PendingDemoStamp {
  employeeId: string;
  /** Every distinct solution tried since the last flush, oldest first. */
  codes: string[];
  /** Epoch ms of the most recent entry, for the staleness window. */
  at: number;
}

/** Beyond this a queued stamp is dropped rather than reported as if it just happened. */
const PENDING_DEMO_STAMP_MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000;

function readStamp(): PendingDemoStamp | null {
  try {
    const raw = localStorage.getItem(PENDING_DEMO_STAMP_KEY);
    if (!raw) return null;
    const stamp = JSON.parse(raw) as Partial<PendingDemoStamp>;
    if (!stamp?.employeeId || !Array.isArray(stamp.codes) || !stamp.codes.length) return null;
    return { employeeId: stamp.employeeId, codes: stamp.codes, at: stamp.at ?? 0 };
  } catch {
    return null; // absent, unparseable, or storage unavailable
  }
}

/**
 * Queue "this person tried a demo" for their REAL Userpilot profile, to be sent the next time they
 * are identified OUTSIDE the sandbox (App.identifyUserpilot).
 *
 * Deferred rather than sent at click time because the old inline `Userpilot.identify` fired
 * microseconds before enterPreview's hard navigation, so the request was routinely cancelled on
 * unload — and anything the SDK buffered locally was then wiped by the `Userpilot.reset()` on
 * preview boot (index.tsx). Inside the sandbox the prospect is an anonymous per-session id, so the
 * real profile cannot be written until they are back in the normal app.
 *
 * localStorage, not sessionStorage, so a prospect who closes the tab mid-demo is still recorded on
 * their next session. That outlives the login, hence the two guards: the stamp is bound to the
 * employeeId that earned it (otherwise a shared browser — or staff dropping an impersonation —
 * would write a demo onto someone who never ran one, and lose the prospect's), and the queue goes
 * stale if it is not flushed. The window runs from the MOST RECENT demo, so someone still trying
 * demos keeps their earlier ones queued; it only drops a queue nobody came back for.
 */
export function recordPendingDemoStamp(solutionCode: string, employeeId: string): void {
  if (!solutionCode || !employeeId) return;
  // Accumulate for the same person so trying several demos before returning reports all of them. A
  // different employee replaces the queue — one browser key, and the person using it now is the one
  // whose signal we can still attribute correctly.
  const existing = readStamp();
  const sameUser = existing?.employeeId === employeeId;
  const codes = sameUser
    ? [...new Set([...(existing as PendingDemoStamp).codes, solutionCode])]
    : [solutionCode];
  try {
    localStorage.setItem(
      PENDING_DEMO_STAMP_KEY,
      JSON.stringify({ employeeId, codes, at: Date.now() } satisfies PendingDemoStamp)
    );
  } catch {
    /* private mode / quota — the demo itself must not break over a telemetry stamp */
  }
}

/**
 * The demos `employeeId` has tried and not yet had reported, oldest first — or null if there are
 * none, they belong to someone else, or the queue has gone stale. Does not clear on success: the
 * caller clears only once the send has actually succeeded, so a failed identify retries on the next
 * load rather than silently burning the signal.
 */
export function peekPendingDemoStamp(employeeId: string): string[] | null {
  const stamp = readStamp();
  if (!stamp || stamp.employeeId !== employeeId) return null;
  if (Date.now() - stamp.at > PENDING_DEMO_STAMP_MAX_AGE_MS) {
    clearPendingDemoStamp(); // months-old intent is noise, not a signal
    return null;
  }
  return stamp.codes;
}

export function clearPendingDemoStamp(): void {
  try {
    localStorage.removeItem(PENDING_DEMO_STAMP_KEY);
  } catch {
    /* nothing to do — a stamp we cannot clear is re-sent at worst */
  }
}

// --- "Show my team" toggle -------------------------------------------------------------

/** The toggle is offered only when the REAL logged-in user was a training manager (captured at entry). */
export function isMyTeamAvailable(): boolean {
  return readIntent()?.realIsTrainingManager === true;
}

/** True while the armed session is showing the TM's real team rather than the synthetic fixture. */
export { isMyTeamActive } from '../mock/previewMode';

/**
 * Flip the armed preview between the synthetic sample (`aviation` …) and the TM's real team
 * (`my-team`) by rewriting the intent's dataset and hard-navigating. The hard load is required:
 * bootstrap re-derives arming from the new dataset — my-team does its un-armed real read first,
 * the synthetic set arms synchronously — and module state (the in-memory runtime dataset) resets.
 *
 * My-team always lands on People (`/team`) — the natural first view of the prospect's real team;
 * the synthetic sample returns to its solution's mapped tab. (My-team's Competencies/Tasks tabs are
 * shown but empty, so they are reachable from People; they are just not the entry point.)
 *
 * `replace`, not `assign`: same hard load, but a hard navigation is a browser-level push that the
 * armed session's pushState suppression cannot see, and toggling would otherwise strand the
 * pre-toggle demo URL in history for `navigate(-1)` to land on after the demo ends.
 */
export function switchPreviewDataset(datasetCode: string): void {
  const intent = readIntent();
  if (!intent || intent.datasetCode === datasetCode) return;
  writeIntent({ ...intent, datasetCode });
  window.location.replace(
    datasetCode === MY_TEAM_DATASET_CODE
      ? withDemoParam('/team', intent.solutionCode, 'own') // keep the tour targeted across the toggle
      : targetPathFor(intent.solutionCode, 'sample')
  );
}

/**
 * Pure decision for the back/forward-cache guard: should a restored page hard-reload?
 * A page restored from bfcache keeps its FROZEN module state (the armed flag + the registered
 * fail-closed mock) and does NOT re-run primeArmedFromIntent. So after exiting a demo (intent
 * cleared) and pressing Back, the armed preview reappears as a stale "zombie" until a manual
 * refresh. If a restored page is still armed but the intent is gone, it must hard-reload so the
 * normal bootstrap re-derives a clean, un-armed app. (Navigating within an ACTIVE demo keeps the
 * intent, so this returns false and the demo is preserved.)
 */
export function shouldReloadOnPageShow(persisted: boolean): boolean {
  return persisted && isPreviewArmed() && !readIntent();
}

/** Registers the bfcache guard. Idempotent. Call once at bootstrap. */
let bfcacheGuardInstalled = false;
export function installPreviewBfcacheGuard(): void {
  if (bfcacheGuardInstalled) return;
  bfcacheGuardInstalled = true;
  window.addEventListener('pageshow', (event) => {
    if (shouldReloadOnPageShow((event as PageTransitionEvent).persisted)) window.location.reload();
  });
}

/** Capture real identity, persist intent, and hard-reload into the preview sandbox. */
export function enterPreview(
  user:
    | {
        fullName: string;
        firstName: string;
        lastName: string;
        email: string;
        employeeId: string;
        isTrainingManager?: boolean;
      }
    | undefined,
  organization: { name: string } | undefined,
  solutionCode: string,
  source: DemoSource
): void {
  if (!user?.employeeId) return; // guard against AppContext's default empty-object user
  writeIntent({
    solutionCode,
    datasetCode: datasetCodeForSolution(solutionCode),
    identity: {
      fullName: user.fullName,
      firstName: user.firstName,
      lastName: user.lastName,
      email: user.email,
      employeeId: user.employeeId,
      orgName: organization?.name ?? '',
    },
    // Remember where the prospect launched from (usually /solution-finder) so Exit returns them
    // there rather than dumping them on the app's default landing (team/company).
    returnTo: window.location.pathname + window.location.search,
    realIsTrainingManager: user.isTrainingManager === true,
  });
  // Queue the "tried a demo" signal for the prospect's REAL Userpilot profile. Deliberately NOT sent
  // here — see recordPendingDemoStamp; App.identifyUserpilot flushes it once they are back outside
  // the sandbox. Both the "Try it now" and the "Switch to demo data" flows funnel through here.
  // (Tour targeting does not depend on this at all: tours match the `demo` URL param, demoParam.ts.)
  recordPendingDemoStamp(solutionCode, user.employeeId);
  // PostHog counterpart of that stamp, and the last moment a launch can be captured live: the
  // sandbox runs with capturing opted out (index.tsx), so nothing after the reload below reaches
  // PostHog. It relies on posthog-js flushing its queue on pagehide; `demo_data:viewed` is the
  // durable backstop, so a launch lost on unload is still counted when the prospect comes back.
  track(DemoDataEvent.Entered, { solutionCode, source });
  // Hard reload into the /try entry; bootstrap re-arms from the intent and TryPage redirects to
  // the real target page (which renders with the normal app chrome).
  //
  // Use replace (not assign): the launch page is usually the Solution Finder, which is a full-screen
  // dialog closed via navigate(-1). If we PUSHED /try on top of it, exiting back to /solution-finder
  // would leave TWO solution-finder history entries, so its close button would land on the second
  // one and reopen (the "close twice" bug). replace keeps a single, clean entry for that slot.
  window.location.replace(`/try/${solutionCode}`);
}

/**
 * Fully tear down the preview and hard-reload back into the REAL app. Reads the return URL BEFORE
 * clearing the intent. The hard reload re-runs bootstrap with no intent → not armed → the mock is
 * never registered and real API + auth (token, user-switch) work normally again.
 *
 * Uses location.replace (not assign) so the armed preview page is REMOVED from history — otherwise
 * it lingers in the back/forward cache and pressing Back/X would restore the live armed demo. With
 * replace, Back goes to the page before the demo. (The pageshow guard above is the backstop for any
 * other armed page still in the bfcache.)
 */
export function exitPreview(): void {
  const returnTo = readIntent()?.returnTo || '/solution-finder';
  clearIntent();
  disarmPreview();
  setRuntimeDataset(null); // drop any real my-team data from memory on exit
  window.location.replace(returnTo);
}
